import { getNestedProjectPaths } from '@app/shared/utils/common-utils';
import {
  ARRAY_OF_OBJECT_PATH_SEPARATOR,
  NESTED_FIELD_SEPARATOR,
  TRANSLATION_OBJECT_FIELD,
} from '@config/global-constants';
import { II18nConfig, ServiceIdentifyType } from '@shared/types';
import {
  chain,
  compact,
  find,
  get,
  has,
  includes,
  intersection,
  isArray,
  isEmpty,
  isEqual,
  join,
  map,
  merge,
  some,
  startsWith,
} from 'lodash';

import { SUPPORT_USER_DEFINED_I18N_CONFIG } from './constants/user-defined/support-user-defined-i18n.config';
import { I18nUserDefinedConfig } from './i18n-user-defined.types';

export const customEntityTypeMapper = (
  identify: ServiceIdentifyType,
): ServiceIdentifyType => {
  switch (identify) {
    default:
      return identify;
  }
};

export const doesEntitySupportUserDefineString = (
  identify: ServiceIdentifyType,
): boolean =>
  has(SUPPORT_USER_DEFINED_I18N_CONFIG, customEntityTypeMapper(identify));

export const getI18nConfigAlias = (
  i18nUserConfig: I18nUserDefinedConfig,
): string[] => get(i18nUserConfig, 'alias', []);

export const getUserDefinedConfig = (
  identify: ServiceIdentifyType,
  i18nConfig?: II18nConfig,
): I18nUserDefinedConfig | null => {
  const userDefinedConfig: I18nUserDefinedConfig =
    get(
      SUPPORT_USER_DEFINED_I18N_CONFIG,
      customEntityTypeMapper(identify),
      null,
    ) ??
    find(SUPPORT_USER_DEFINED_I18N_CONFIG, (configValue) =>
      includes(getI18nConfigAlias(configValue), identify),
    );

  /**
   * Return userDefinedConfig directly if it's an array and i18nConfig is empty
   */
  if (isArray(userDefinedConfig) && isEmpty(i18nConfig)) {
    return userDefinedConfig;
  }

  return merge(
    {},
    isArray(userDefinedConfig)
      ? <I18nUserDefinedConfig>{
          userDefinedPaths: userDefinedConfig,
        }
      : userDefinedConfig,
    isEmpty(i18nConfig)
      ? {}
      : <I18nUserDefinedConfig>{
          specialI18nConfig: i18nConfig,
        },
  );
};

export const getUserDefineStringPaths = (
  identify: ServiceIdentifyType,
  i18nConfig?: II18nConfig,
): string[] => {
  const userDefinedConfig = getUserDefinedConfig(identify, i18nConfig);

  const paths =
    (isArray(userDefinedConfig)
      ? userDefinedConfig
      : userDefinedConfig?.userDefinedPaths) ?? [];

  const pathPrefix = i18nConfig?.pathPrefix;

  return isEmpty(pathPrefix)
    ? paths
    : map(paths, (path) =>
        join(
          [...(isArray(pathPrefix) ? pathPrefix : [pathPrefix]), path],
          NESTED_FIELD_SEPARATOR,
        ),
      );
};

export const getUserDefineStringAlias = (
  identify: ServiceIdentifyType,
): string[] => getI18nConfigAlias(getUserDefinedConfig(identify));

export const getNormalizedUserDefinePaths = (
  identify: ServiceIdentifyType,
): string[] => {
  const userDefinedPaths = getUserDefineStringPaths(identify);

  return map(userDefinedPaths, (path) =>
    path?.split(`.${ARRAY_OF_OBJECT_PATH_SEPARATOR}.`)?.join('.'),
  );
};

export const getI18nConfigFromUserDefinedConfig = (
  identify: ServiceIdentifyType,
  specialI18nConfig?: II18nConfig,
): II18nConfig | null => {
  const userDefinedConfig = getUserDefinedConfig(identify, specialI18nConfig);

  return isArray(userDefinedConfig)
    ? null
    : userDefinedConfig?.specialI18nConfig;
};

export const getAddedI18nNestedPaths = (
  identify: ServiceIdentifyType,
  nestedProjectPaths: string[],
  options?: { specialLocale?: string },
): string[] => {
  if (isEmpty(nestedProjectPaths)) {
    return [];
  }

  const userDefinedNestedPaths = getNestedProjectPaths(
    getNormalizedUserDefinePaths(identify),
  );

  if (isEmpty(userDefinedNestedPaths)) {
    return [];
  }

  const needCheckI18nProjectPaths = intersection(
    getNestedProjectPaths(nestedProjectPaths),
    userDefinedNestedPaths,
  );

  const addedI18nProjects = chain(needCheckI18nProjectPaths)
    .uniq()
    .map((needCheckPath) => {
      const i18nProjectPath = `${needCheckPath}.${TRANSLATION_OBJECT_FIELD}`;

      // Check if the i18n project path is already included in the nested paths or is the same with the needCheckPath
      return some(
        nestedProjectPaths,
        (nestedProjectPath) =>
          isEqual(nestedProjectPath, needCheckPath) ||
          isEqual(nestedProjectPath, i18nProjectPath) ||
          startsWith(nestedProjectPath, i18nProjectPath),
      )
        ? null
        : compact([i18nProjectPath, options?.specialLocale ?? '']).toString();
    })
    .compact()
    .value();

  return addedI18nProjects;
};
